import React, {useState} from 'react'
import {
  Badge,
  Box,
  Chip,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import {useNavigate} from 'react-router-dom'
import {
  useGetReleaseNotesQuery,
  useGetReleaseNotesUnreadCountQuery,
  useMarkReleaseNotesSeenMutation,
} from '../redux/commonRTK'
import {BORDER, PRIMARY_BLUE, SUCCESS, WARNING, ERROR, INFO, SUCCESS_BG, WARNING_BG, ERROR_BG, INFO_BG} from '../styles/foundation/colors'

const TYPE_COLOR = {
  feature: {bg: INFO_BG, fg: INFO},
  fix: {bg: WARNING_BG, fg: WARNING},
  improvement: {bg: SUCCESS_BG, fg: SUCCESS},
  breaking: {bg: ERROR_BG, fg: ERROR},
}

const formatDate = (val) => {
  if (!val) return ''
  const d = new Date(val)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'})
}

export default function ReleaseNotesBell() {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)

  // RTK Query — cached across route changes, deduped, refetches only on tag invalidation.
  const {data: unreadCount = 0} = useGetReleaseNotesUnreadCountQuery()
  const {data: notesData} = useGetReleaseNotesQuery({limit: 5})
  const [markSeen] = useMarkReleaseNotesSeenMutation()

  const items = notesData?.items || []
  const latest = items[0] || null
  const totalCount = notesData?.count ?? items.length

  const handleClick = (e) => setAnchorEl(e.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const goToList = () => {
    handleClose()
    if (unreadCount > 0) markSeen()
    navigate('/whats-new')
  }

  const open = Boolean(anchorEl)
  const type = (
    latest?.changeType ||
    latest?.type ||
    latest?.changes?.[0]?.type ||
    ''
  ).toString()
  const typeColor = TYPE_COLOR[type.toLowerCase()] || {bg: INFO_BG, fg: PRIMARY_BLUE}

  return (
    <>
      <Tooltip title="What's New">
        <IconButton
          onClick={handleClick}
          size='small'
          sx={{
            color: '#637381',
            borderRadius: '10px',
            '&:hover': {bgcolor: 'rgba(99,115,129,0.10)', color: '#212B36'},
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color='error'
            overlap='circular'
            max={99}
            invisible={!unreadCount}
          >
            <CampaignOutlinedIcon sx={{fontSize: 22}} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        PaperProps={{
          sx: {
            width: 360,
            borderRadius: 2,
            overflow: 'hidden',
            mt: 1,
          },
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            color: '#fff',
            p: 2,
          }}
        >
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CampaignOutlinedIcon sx={{color: '#fff'}} />
            </Box>
            <Box>
              <Typography variant='subtitle1' fontWeight={700} sx={{lineHeight: 1.2}}>
                What's New
              </Typography>
              <Typography variant='caption' sx={{color: 'rgba(255,255,255,0.85)'}}>
                Latest updates &amp; improvements
              </Typography>
            </Box>
          </Stack>
        </Box>

        {latest ? (
          <Box sx={{p: 2, cursor: 'pointer'}} onClick={goToList}>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{mb: 1, flexWrap: 'wrap', rowGap: 0.5}}
            >
              {latest.version && (
                <Chip
                  label={latest.version}
                  size='small'
                  variant='outlined'
                  sx={{fontFamily: 'monospace', fontSize: 10, height: 20}}
                />
              )}
              {type && (
                <Chip
                  label={type.charAt(0).toUpperCase() + type.slice(1)}
                  size='small'
                  sx={{
                    backgroundColor: typeColor.bg,
                    color: typeColor.fg,
                    fontWeight: 700,
                    fontSize: 10,
                    height: 20,
                  }}
                />
              )}
              <Box sx={{flex: 1}} />
              <Typography variant='caption' color='text.secondary'>
                {formatDate(latest.releaseDate || latest.publishedAt || latest.createdAt)}
              </Typography>
            </Stack>
            <Typography
              variant='subtitle2'
              fontWeight={700}
              color='text.primary'
              sx={{mb: 0.5}}
            >
              {latest.title || latest.headline}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {latest.summary || latest.description}
            </Typography>
          </Box>
        ) : (
          <Box sx={{p: 3, textAlign: 'center'}}>
            <Typography variant='body2' color='text.secondary'>
              No release notes available
            </Typography>
          </Box>
        )}

        <Box
          onClick={goToList}
          sx={{
            borderTop: '1px solid',
            borderColor: BORDER,
            backgroundColor: '#fafbfc',
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': {backgroundColor: '#f3f4f6'},
          }}
        >
          <Typography variant='caption' color='text.secondary' fontWeight={600}>
            {totalCount > 0
              ? `${totalCount} ${totalCount === 1 ? 'latest update' : 'latest updates'}`
              : 'No updates'}
          </Typography>
          <Typography variant='caption' fontWeight={700} sx={{color: PRIMARY_BLUE}}>
            View all ›
          </Typography>
        </Box>
      </Popover>
    </>
  )
}
